import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

const ALLOWED_IMAGE_ACCEPT = ".png,.jpg,.jpeg,image/png,image/jpeg";
const PHOTO_VIDEO_ACCEPT = `${ALLOWED_IMAGE_ACCEPT},video/*`;

const isAllowedImageFile = (file) => {
  const type = String(file?.type || "").toLowerCase();
  if (type === "image/png" || type === "image/jpeg") return true;
  return /\.(png|jpe?g)$/i.test(String(file?.name || ""));
};

const partitionMediaFiles = (files) => {
  const allowed = [];
  let hasRejectedImage = false;

  files.forEach((file) => {
    if (file.type?.startsWith("video/")) {
      allowed.push(file);
      return;
    }
    if (isAllowedImageFile(file)) {
      allowed.push(file);
      return;
    }
    if (file.type?.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|heic|heif|svg)$/i.test(file.name || "")) {
      hasRejectedImage = true;
    }
  });

  return { allowed, hasRejectedImage };
};

export default function AttachmentModal({ open, onClose, onPick, anchorRef, onFileSelect, groupId }) {  const modalRef = useRef();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [showCamera, setShowCamera] = useState(false);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    if (open) {
      setActiveTab("all");
    }
  }, [open]);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target) &&
        anchorRef.current &&
        !anchorRef.current.contains(event.target)
      ) {
        onClose();
      }
    }
    if (open) {
      const timeoutId = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 100);
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [open, onClose, anchorRef]);
const stopCameraStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };
  // Cleanup camera stream on unmount
  useEffect(() => () => stopCameraStream(), []);

  if (!open) return null;

  // Keep the menu anchored to the tapped button without overflowing mobile screens.
  const getModalStyle = () => {
    if (typeof window === "undefined" || !anchorRef.current) {
      return { bottom: "72px", left: "12px" };
    }

    const rect = anchorRef.current.getBoundingClientRect();
    const margin = 12;
    const modalWidth = Math.min(320, window.innerWidth - margin * 2);
    const left = Math.min(
      Math.max(rect.left + rect.width / 2 - modalWidth / 2, margin),
      window.innerWidth - modalWidth - margin
    );
    const bottom = Math.max(window.innerHeight - rect.top + 10, margin);
    const maxHeight = Math.max(180, rect.top - margin * 2);

    return {
      bottom: `${bottom}px`,
      left: `${left}px`,
      width: `${modalWidth}px`,
      maxHeight: `${maxHeight}px`,
    };
  };

  const modalStyle = getModalStyle();

  const handleFileChange = (type, event, shareTypeOverride = "") => {
    let files = Array.from(event.target.files || []);
    if (files.length === 0) {
      event.target.value = "";
      return;
    }

    if (type === "photo" || type === "camera") {
      const { allowed, hasRejectedImage } = partitionMediaFiles(files);
      if (hasRejectedImage) {
        toast.error("Only PNG, JPG, and JPEG images are allowed.");
      }
      files = allowed;
      if (files.length === 0) {
        event.target.value = "";
        return;
      }
    }

    if (!groupId) {      toast.error("No group selected");
      event.target.value = "";
      return;
    }

    if (onFileSelect) {
      // Determine a more specific type based on actual file MIME types
      if (files.length === 1) {
        const f = files[0];
        const chosenType = f.type?.startsWith("video") ? "video" : (f.type?.startsWith("image") ? type : type);
        onFileSelect(f, chosenType, shareTypeOverride);
      } else {
        const allImages = files.every(f => f.type?.startsWith("image"));
        const allVideos = files.every(f => f.type?.startsWith("video"));
        const chosenType = allImages ? "photo" : (allVideos ? "video" : undefined);
        onFileSelect(files, chosenType, shareTypeOverride);
      }
    }

    onClose();
    event.target.value = "";
  };

  

  const openCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Camera is not supported on this device");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      setShowCamera(true);
      requestAnimationFrame(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      });
    } catch (err) {
      toast.error("Unable to access camera");
    }
  };

  const closeCamera = () => {
    stopCameraStream();
    setShowCamera(false);
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (!blob) {
        toast.error("Could not capture photo");
        return;
      }
      const file = new File([blob], "camera-photo.jpg", { type: "image/jpeg" });
      if (!groupId) {
        toast.error("No group selected");
        closeCamera();
        return;
      }
      onFileSelect?.(file, "camera", activeTab === "once" ? "sharetoview" : "");
      closeCamera();
      onClose();
    }, "image/jpeg", 0.92);
  };

  return (
    <div
      ref={modalRef}
      className="bg-white rounded-2xl shadow-2xl fixed z-50 border border-gray-100 overflow-y-auto"
      style={modalStyle}
    >
      <div className="border-b border-gray-100 bg-[#FAFAFF] px-4 pt-4">
        <div className="grid grid-cols-2 gap-2 rounded-2xl bg-gray-100 p-1">
          <button
            type="button"
            onClick={() => setActiveTab("all")}
            className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${activeTab === "all" ? "bg-[#7C4DFF] text-white shadow" : "text-gray-700"}`}
          >
            All media
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("once")}
            className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${activeTab === "once" ? "bg-[#7C4DFF] text-white shadow" : "text-gray-700"}`}
          >
            View once
          </button>
        </div>
      </div>

      <div className="px-4 py-4">
        <div className="grid grid-cols-2 gap-3">
          {activeTab === "all" && (
          <label className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-gray-100 bg-white p-3 text-center shadow-sm hover:border-[#7C4DFF]/30 hover:bg-[#F7F3FF] transition cursor-pointer">
            <input
              type="file"
              accept=".pdf,.doc,.docx,.txt,.rtf,.ppt,.pptx,.xls,.xlsx,.csv,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/rtf,application/rtf"
              style={{ display: "none" }}
              onChange={(e) => handleFileChange("document", e, "")}
            />
            <img src="../chatscreen/document.svg" alt="document" className="w-7 h-7" />
            <span className="text-sm font-medium text-gray-900">Documents</span>
          </label>
          )}

          {activeTab === "all" && (
            <button
              type="button"
              className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-gray-100 bg-white p-3 text-center shadow-sm hover:border-[#7C4DFF]/30 hover:bg-[#F7F3FF] transition"
              onClick={() => {
                onPick("poll");
                onClose();
              }}
            >
              <img src="../groupscreen/poll.svg" alt="poll" className="w-7 h-7" />
              <span className="text-sm font-medium text-gray-900">Create a poll</span>
            </button>
          )}

          <label className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-gray-100 bg-white p-3 text-center shadow-sm hover:border-[#7C4DFF]/30 hover:bg-[#F7F3FF] transition cursor-pointer">
            <input
              type="file"
              accept={PHOTO_VIDEO_ACCEPT}
              multiple
              style={{ display: "none" }}
              onChange={(e) => handleFileChange("photo", e, activeTab === "once" ? "sharetoview" : "")}
            />
            <img src="../chatscreen/photo.svg" alt="phot" className="w-7 h-7" />
            <span className="text-sm font-medium text-gray-900">Photo & video</span>
            {activeTab === "once" && <span className="text-[11px] font-semibold text-red-500">View once</span>}
          </label>

          <label
            className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-gray-100 bg-white p-3 text-center shadow-sm hover:border-[#7C4DFF]/30 hover:bg-[#F7F3FF] transition cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              openCamera();
            }}
          >
            <input
              type="file"
              accept={ALLOWED_IMAGE_ACCEPT}
              capture="environment"
              style={{ display: "none" }}
              onChange={(e) => handleFileChange("camera", e, activeTab === "once" ? "sharetoview" : "")}
            />
            <img src="../chatscreen/camera.svg" alt="camera" className="w-7 h-7" />
            <span className="text-sm font-medium text-gray-900">Camera</span>
            {activeTab === "once" && <span className="text-[11px] font-semibold text-red-500">View once</span>}
          </label>
        </div>
      </div>

      {showCamera && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-2xl p-4 shadow-2xl w-[360px] max-w-[90%] flex flex-col gap-3">
            <div className="rounded-xl overflow-hidden bg-black">
              <video ref={videoRef} className="w-full h-[220px] object-cover" autoPlay muted playsInline />
            </div>
            <div className="flex justify-center gap-3">
              <button
                type="button"
                onClick={capturePhoto}
                className="submit-button px-6"
              >
                Capture
              </button>
              <button
                type="button"
                onClick={closeCamera}
                className="border border-[#7A5AF8] text-[#7A5AF8] px-4 py-2 rounded-[14px]"
              >
                Cancel
              </button>
            </div>
          </div>
          <canvas ref={canvasRef} className="hidden" />
        </div>
      )}
    </div>
  );
}